import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BadgeCheckIcon, CameraIcon, CheckIcon, ClipboardListIcon, HandshakeIcon, MapPinIcon, ShieldCheckIcon, ShieldOffIcon, TrendingUpIcon, WalletIcon } from 'lucide-react';
import { Logo } from '../../components/shell/Logo';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ApplicationWizard } from '../../components/network/ApplicationWizard';
import { useNetwork } from '../../contexts/NetworkContext';
import { CAMPUS_INTAKE } from '../../types/network';
import type { CampusRecruitmentStatus } from '../../types/network';
import type { BadgeTone } from '../../components/ui/Badge';

/**
 * Four distinct operational events, deliberately not collapsed into one card.
 * Verification, acquisition, landlord onboarding and business acquisition create
 * different records and carry different reward rules.
 */
const WHAT_THEY_DO = [{
  icon: CameraIcon,
  title: 'Verify accommodation in person',
  body: 'Stand at the property, capture location and photographs, and record what is actually there. A reviewer checks the evidence before anything is marked Campuzon Verified.'
}, {
  icon: HandshakeIcon,
  title: 'Acquire properties and onboard landlords',
  body: 'Finding a landlord is its own piece of work. You create the lead, operations make contact, and onboarding is tracked separately from the inspection that follows.'
}, {
  icon: ClipboardListIcon,
  title: 'Bring local businesses on',
  body: 'Hostels, food vendors, laundries, printers, transport. Discovered, contacted, onboarded, verified — each stage recorded, each with its own reward.'
}, {
  icon: MapPinIcon,
  title: 'Build campus intelligence',
  body: 'Rents, fares, new supply, what students cannot find. Structured observations that are reviewed and given an expiry before anyone relies on them.'
}];
const GROWTH = ['Campus Representative', 'Senior Representative', 'Area Lead', 'Campus Lead', 'Regional Lead', 'National Campus Lead'];
const HOW_WORK_HAPPENS = [{
  title: 'Accept a task',
  body: 'It states the location, the required evidence and the reward rule before you take it.'
}, {
  title: 'Go to the location',
  body: 'Field work happens in person. There is no way to complete it from your room.'
}, {
  title: 'Capture evidence',
  body: 'Location, timestamp, photographs, property data, owner details, availability.'
}, {
  title: 'Submit',
  body: 'Submitting is not verifying. Your submission shows you any flags it raises.'
}, {
  title: 'Campuzon reviews it',
  body: 'Approved, returned for rework, or rejected — with a reason you can dispute.'
}, {
  title: 'Approved work enters your ledger',
  body: 'One entry, tied to the rule that produced it. Nothing is paid on submission.'
}, {
  title: 'Finance releases eligible payouts',
  body: 'Approved, then settled through a licensed payment provider.'
}];
const CONSOLE_PREVIEW = [{
  label: 'Tasks',
  body: 'Assigned, available, in progress, submitted, rework, completed.'
}, {
  label: 'Field capture',
  body: 'Location, camera, structured forms, offline queue.'
}, {
  label: 'Earnings',
  body: 'Pending, approved, paid — every entry traced to an activity.'
}, {
  label: 'Performance',
  body: 'Accuracy, rework rate, reliability, and what to improve.'
}, {
  label: 'Referrals',
  body: 'Your code, link and QR, with clicks and conversions.'
}, {
  label: 'Intelligence',
  body: 'Submit observations and watch them through review.'
}];
const NEVER_EXPECTED = ['Pay to access a task, or pay an "inspection fee"', 'Present yourself as a university officer or agent', 'Ask a student or landlord for a password', 'Collect personal information a task does not require', 'Enter premises that feel unsafe', 'Approve your own verification'];
const AFTER_YOU_APPLY = ['Application received', 'Operations review', 'Shortlist, and an interview if the campus needs one', 'Identity confirmation', 'Agreement, conduct and privacy acknowledgement', 'Core training, with assessments', 'Campus and area assignment', 'Activation, then your first task'];
const FAQS = [{
  q: 'How much can I earn?',
  a: 'You earn on verified work, referrals, transactions and performance — under published reward rules that state the amount for each activity before you accept it. We do not advertise a monthly figure, because what you earn depends on what you complete and what passes review.'
}, {
  q: 'When do I get paid?',
  a: 'An earning is created when a reviewer approves your work, approved by finance, then released in a settlement window through a licensed payment provider. Every step is visible in your console.'
}, {
  q: 'Do I represent the university?',
  a: 'No. You represent Campuzon. You may never present yourself as an officer, agent or representative of any institution, and doing so ends your place on the network.'
}, {
  q: 'Can I be a student and a representative at the same time?',
  a: 'Yes. It is one Campuzon account with two roles. Your student life — academics, notes, diary, wellbeing — stays entirely separate from your representative work, and operations can never see it.'
}, {
  q: 'What do I need to start?',
  a: 'A smartphone with a working camera, enough data to upload evidence, a few hours a week, and a willingness to be accurate rather than fast.'
}, {
  q: 'Is it safe?',
  a: 'Safety training is required, some task categories are restricted, and you can flag a location as unsafe or report an incident confidentially at any time. Never pay an "inspection fee" — no legitimate agent charges one.'
}];
function statusTone(status: CampusRecruitmentStatus): BadgeTone {
  if (status === 'Active' || status === 'Recruiting') return 'ok';
  if (status === 'Pilot') return 'brand';
  if (status === 'Closed') return 'danger';
  if (status === 'Paused') return 'warn';
  return 'neutral';
}
export function RepresentativeProgramme() {
  const network = useNetwork();
  const [applying, setApplying] = useState(false);
  const [submitted, setSubmitted] = useState<{
    id: string;
    held: boolean;
  } | null>(null);
  return <div className="min-h-full w-full bg-canvas" data-id="element-9718" data-mp="2h0g8bvtz43sq">
      <header className="border-b border-line bg-surface" data-id="element-9719" data-mp="2gx238ftxdghl">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-4 sm:px-6" data-id="element-9720" data-mp="34qaiu6qtlg9t">
          <Logo subtitle="Campus Network" data-id="element-9721" data-mp="39n102xed2hwt" />
          <div className="ml-auto flex items-center gap-2" data-id="element-9722" data-mp="39lzj6canz176">
            <Link to="/" className="text-[13px] font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-9723" data-mp="2vnj49y2ne4dy">
              Campuzon for students
            </Link>
            <Button variant="primary" size="sm" onClick={() => setApplying(true)} data-id="element-9724" data-mp="1kb8kui9snkm6">
              Apply
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-id="element-9725" data-mp="1gq6p3b0gg743">
        {/* hero */}
        <section className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-start" data-id="element-9726" data-mp="1bswulgy7l59e">
          <div data-id="element-9727" data-mp="1qpxnvow1t89z">
            <Badge tone="brand" icon={<ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-9729" data-mp="1qow14n5o6xcf" />} data-id="element-9728" data-mp="1qpnt7o026cxy">
              Authorised field operators, not promoters
            </Badge>
            <h1 className="mt-4 text-4xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-5xl" data-id="element-9730" data-mp="1logknmq3d9q8">
              Become a Campuzon Campus Representative
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-ink-muted" data-id="element-9731" data-mp="t3gpegbkwomn">
              Earn money, build professional experience, and help students make better decisions about
              accommodation, services and life around campus.
            </p>
            <p className="mt-3 max-w-xl text-[13px] leading-6 text-ink-muted" data-id="element-9732" data-mp="f9fphj8w4vkp">
              You earn based on verified work, referrals, transactions and performance. Every reward
              rule is published before you accept a task, and an earning only exists once a reviewer has
              approved what you submitted.
            </p>
            <div className="mt-6 flex flex-wrap gap-3" data-id="element-9733" data-mp="3pyegv1ea2rnd">
              <Button variant="primary" icon={<ArrowRightIcon className="h-4 w-4" data-id="element-9735" data-mp="2rm7qkzib28d" />} onClick={() => setApplying(true)} data-id="element-9734" data-mp="3uubwt6lmpvzm">
                Apply to become a representative
              </Button>
              <a href="#how" data-id="element-9736" data-mp="7ounkex6mu6g">
                <Button variant="secondary" data-id="element-9737" data-mp="cp8qjijxfdxm">Read what the role involves</Button>
              </a>
            </div>
          </div>

          <div className="rounded-card border border-line bg-surface px-5 py-5" data-id="element-9738" data-mp="hh0gprz67hn8">
            <p className="text-[13px] font-semibold text-ink" data-id="element-9739" data-mp="mejsml8ebic0">The campus network</p>
            <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-9740" data-mp="qhoseg13d5wy">
              Status comes from each campus’s configuration, not from this page.
            </p>
            <ul className="mt-3 divide-y divide-line" data-id="element-9741" data-mp="vdx8bvn45356">
              {network.campuses.map(campus => {
              const intake = CAMPUS_INTAKE[campus.recruitmentStatus];
              return <li key={campus.id} className="py-2.5 first:pt-0" data-id="element-9742" data-mp="26xma3wevhk7o">
                    <div className="flex flex-wrap items-center gap-2" data-id="element-9743" data-mp="26xvzpiw09pcu">
                      <span className="min-w-0 flex-1" data-id="element-9744" data-mp="21zui72e89y3h">
                        {campus.published ? <Link to={`/network/${campus.id}`} className="block text-[13px] font-medium text-ink hover:underline" data-id="element-9745" data-mp="2ynie3t7awjsy">
                            {campus.name}
                          </Link> : <span className="block text-[13px] font-medium text-ink" data-id="element-9746" data-mp="2kx55rpqhz333">
                            {campus.institution}
                          </span>}
                        <span className="block text-[12px] text-ink-muted" data-id="element-9747" data-mp="3dkhtw4cb3iyi">
                          {campus.city}, {campus.state}
                        </span>
                      </span>
                      <Badge tone={statusTone(campus.recruitmentStatus)} data-id="element-9748" data-mp="371z8e0xo1pn9">
                        {campus.recruitmentStatus}
                      </Badge>
                    </div>
                    <p className="mt-0.5 text-[12px] leading-5 text-ink-subtle" data-id="element-9749" data-mp="3gylrolacg8n6">{intake.note}</p>
                  </li>;
            })}
            </ul>
          </div>
        </section>

        {/* what they do */}
        <section className="mt-14" data-id="element-9750" data-mp="3pz0fpsep01ud">
          <h2 className="text-2xl font-semibold tracking-tight text-ink" data-id="element-9751" data-mp="8j233zkmbeeg">What representatives do</h2>
          <ul className="mt-5 grid gap-4 sm:grid-cols-2" data-id="element-9752" data-mp="3jz2qsfl1wct">
            {WHAT_THEY_DO.map(item => {
            const Icon = item.icon;
            return <li key={item.title} className="flex gap-3.5 rounded-card border border-line bg-surface px-5 py-4" data-id="element-9753" data-mp="hjzyjtgzqdlf">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand-700" data-id="element-9754" data-mp="cisqdtaxrce8" />
                  <div data-id="element-9755" data-mp="w78fzc0w7f5u">
                    <p className="text-[14px] font-semibold text-ink" data-id="element-9756" data-mp="w4mbjl9atfru">{item.title}</p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-9757" data-mp="113fn3850ywpd">{item.body}</p>
                  </div>
                </li>;
          })}
          </ul>
          <p className="mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-9758" data-mp="r5rfviq8fwqb">
            Growing the network — referring students, supporting campus events — sits alongside all four,
            and progresses into leading an area, then a campus, then a region.
          </p>
        </section>

        {/* how the work actually happens */}
        <section id="how" className="mt-14" data-id="element-9759" data-mp="3fx6jb54gqat">
          <h2 className="text-2xl font-semibold tracking-tight text-ink" data-id="element-9760" data-mp="8hw9ijwy9l78">How the work actually happens</h2>
          <ol className="mt-5 space-y-0" data-id="element-9761" data-mp="3qnit1bdcy3gx">
            {HOW_WORK_HAPPENS.map((step, index) => <li key={step.title} className="flex gap-4" data-id="element-9762" data-mp="3vkiz3ma7j681" data-mpi={index}>
                <div className="flex flex-col items-center" data-id="element-9763" data-mp="215c53hbcchym">
                  <span className="cz-num flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-brand-700 bg-brand-50 text-[12px] font-semibold text-brand-800" data-id="element-9764" data-mp="216n4z61o15kg">
                    {index + 1}
                  </span>
                  {index < HOW_WORK_HAPPENS.length - 1 && <span aria-hidden className="my-1 w-px flex-1 bg-line-strong" data-id="element-9765" data-mp="1nfs4oi58fwsh" />}
                </div>
                <div className="pb-5" data-id="element-9766" data-mp="2f53o5wgpsojh">
                  <p className="text-[14px] font-semibold text-ink" data-id="element-9767" data-mp="2f66cpxqkeew8">{step.title}</p>
                  <p className="mt-0.5 text-[13px] leading-6 text-ink-muted" data-id="element-9768" data-mp="2b307mtf6utrc">{step.body}</p>
                </div>
              </li>)}
          </ol>
          <p className="flex items-start gap-2 rounded-lg border border-line bg-surface px-3.5 py-3 text-[12px] leading-5 text-ink-muted" data-id="element-9769" data-mp="266ja58m3n31l">
            <WalletIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-9770" data-mp="uoqihx34h0zg" />
            Campuzon does not hold your money. Payouts move through a licensed payment provider, and your
            bank details stay with that provider rather than in the representative console.
          </p>
        </section>

        {/* the console */}
        <section className="mt-14 grid gap-4 lg:grid-cols-[1fr_0.8fr]" data-id="element-9771" data-mp="3fci7dheqkrtl">
          <Panel title="Your representative account" description="Approval gives you a real operating console, not a welcome email." data-id="element-9772" data-mp="3k4jtdv1phwwt">
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-9773" data-mp="3p5h4xos7nspc">
              {CONSOLE_PREVIEW.map(item => <li key={item.label} className="rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-9774" data-mp="2vwrr258yrtz">
                  <p className="text-[13px] font-semibold text-ink" data-id="element-9775" data-mp="6xhxm3jd65uo">{item.label}</p>
                  <p className="mt-0.5 text-[12px] leading-5 text-ink-muted" data-id="element-9776" data-mp="6v5lw8478rq2">{item.body}</p>
                </li>)}
            </ul>
            <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-9777" data-mp="28o39npj4cu45">
              It is the same Campuzon account you use as a student. One identity, two roles, and your
              academic life stays private from operations.
            </p>
          </Panel>

          <div className="space-y-4" data-id="element-9778" data-mp="1ncuieu2at3nw">
            <Panel title="What you need" data-id="element-9779" data-mp="1nbt6kndgkd54">
              <ul className="space-y-2" data-id="element-9780" data-mp="1nart6c8yzek8">
                {['A smartphone with a working camera', 'Enough mobile data to upload evidence', 'Five hours a week, or more', 'Current student or recent graduate', 'Willingness to be accurate rather than fast'].map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-9781" data-mp="14fl1pkocy4ac">
                    <CheckIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-9782" data-mp="zlyvobcp8yni" /> {item}
                  </li>)}
              </ul>
            </Panel>
            <Panel title="Growth path" data-id="element-9783" data-mp="6ze1z48r4ags">
              <ol className="space-y-1.5" data-id="element-9784" data-mp="6wi7jpw443x3">
                {GROWTH.map((rank, index) => <li key={rank} className="flex items-center gap-2 text-[13px] text-ink" data-id="element-9785" data-mp="2ikn2tal8nt8q" data-mpi={index}>
                    <span className="cz-num w-4 text-[12px] text-ink-subtle" data-id="element-9786" data-mp="2ne97ogl5ustz">{index + 1}</span>
                    {rank}
                  </li>)}
              </ol>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9787" data-mp="2dnwj8da86fik">
                Each rank carries its own authorisation — what you can see, assign and oversee — decided
                against published criteria, not seniority alone.
              </p>
            </Panel>
          </div>
        </section>

        {/* protection */}
        <section className="mt-14 grid gap-4 lg:grid-cols-2" data-id="element-9788" data-mp="2se3v2a5q90aa">
          <Panel title="You are never expected to" data-id="element-9789" data-mp="2x9j5st8fug2h">
            <ul className="space-y-2" data-id="element-9790" data-mp="1gwm307xidp3l">
              {NEVER_EXPECTED.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-9791" data-mp="1gyyo2tykcfkv">
                  <ShieldOffIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-danger-700" data-id="element-9792" data-mp="1ltchiqto0cmw" /> {item}
                </li>)}
            </ul>
            <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-9793" data-mp="3imt82zl7t7jz">
              Safety training is required before field work. You can flag a location as unsafe, escalate to
              your lead, or report an incident confidentially — and a safety report is never used against
              your performance score.
            </p>
          </Panel>

          <Panel title="What we will not tell you" data-id="element-9794" data-mp="2zva6mx6ua1k2">
            <ul className="space-y-2" data-id="element-9795" data-mp="2zuiho57qz22r">
              {['A monthly earnings figure. What you earn depends on approved work.', 'That this is passive income. It is field work, on foot, in person.', 'That every submission gets paid. Work that fails review does not.', 'That you will represent the university. You represent Campuzon only.'].map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-9796" data-mp="p1ee2v9npnmc">
                  <BadgeCheckIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-9797" data-mp="2bzyppdp3r7n8" /> {item}
                </li>)}
            </ul>
          </Panel>
        </section>

        {/* what happens after you apply */}
        <section className="mt-14" data-id="element-9798" data-mp="2ksigxt32e9y1">
          <h2 className="text-2xl font-semibold tracking-tight text-ink" data-id="element-9799" data-mp="2krqsr3bsrehs">What happens after you apply</h2>
          <ol className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9800" data-mp="1o3ubrxcg6h61">
            {AFTER_YOU_APPLY.map((stage, index) => <li key={stage} className="rounded-card border border-line bg-surface px-4 py-3" data-id="element-9801" data-mp="1x2o5nfye8tfw" data-mpi={index}>
                <span className="cz-num text-[12px] font-semibold text-brand-800" data-id="element-9802" data-mp="221rd171ujbr1">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-9803" data-mp="38hbe1svfanu8">{stage}</p>
              </li>)}
          </ol>
          <p className="mt-3 text-[13px] leading-6 text-ink-muted" data-id="element-9804" data-mp="3dir1tsozj8gb">
            No field work, tasks or earnings are available until every required onboarding step and
            assessment is complete. That is what "authorised" means here.
          </p>
        </section>

        {/* faq */}
        <section className="mt-14" data-id="element-9805" data-mp="2xysblythg1rd">
          <h2 className="text-2xl font-semibold tracking-tight text-ink" data-id="element-9806" data-mp="2t5fq3xk3ob2u">Questions</h2>
          <dl className="mt-5 divide-y divide-line border-y border-line" data-id="element-9807" data-mp="37ulpblixu1aq">
            {FAQS.map(faq => <div key={faq.q} className="py-4" data-id="element-9808" data-mp="32wafvbwk1st1">
                <dt className="text-[14px] font-semibold text-ink" data-id="element-9809" data-mp="32vivyycbljil">{faq.q}</dt>
                <dd className="mt-1 max-w-3xl text-[13px] leading-6 text-ink-muted" data-id="element-9810" data-mp="3gu8t2dz53j6y">{faq.a}</dd>
              </div>)}
          </dl>
        </section>

        {/* application */}
        <section id="apply" className="mt-14" data-id="element-9811" data-mp="1mik7hsyekidl">
          {submitted ? <Panel title={submitted.held ? 'Your application is waitlisted' : 'Your application is under review'} data-id="element-9812" data-mp="1igfg70vffxu4">
              <p className="text-[13px] leading-6 text-ink" data-id="element-9813" data-mp="18cikluv2lh7i">
                Reference <span className="cz-num font-semibold" data-id="element-9814" data-mp="1cdu5jn301e9d">{submitted.id}</span>.{' '}
                {submitted.held ? 'That campus is not open for intake yet, so your application is held rather than reviewed now. We will come back to you when it opens.' : 'Operations review applications campus by campus. You will hear from us by email, and nothing else is expected from you until then.'}
              </p>
              <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-9815" data-mp="tkzwf9s24gd2">
                Your documents are now with operations for identity confirmation. If you are approved, you
                will complete the agreement, conduct, privacy and training steps before any field work is
                available to you.
              </p>
            </Panel> : <ApplicationWizard open={applying} onOpen={() => setApplying(true)} onSubmitted={setSubmitted} data-id="element-9816" data-mp="2ez4e0e3jr6s4" />}
        </section>
      </main>

      <footer className="mt-16 border-t border-line bg-surface" data-id="element-9817" data-mp="3nc3mx95pf71r">
        <div className="mx-auto max-w-6xl px-4 py-6 text-[12px] leading-5 text-ink-muted sm:px-6" data-id="element-9818" data-mp="38mxqfsy93uxk">
          Campuzon Campus Network. Representatives are authorised Campuzon field operators and hold no
          institutional authority. Campuzon Verified reflects an inspection reviewed by Campuzon, not a
          statement of legal ownership.
        </div>
      </footer>
    </div>;
}